import { ReactElement, useCallback } from "react";
import classNames from "classnames";
import { generateTrackingId } from "client/Misc/utils";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { DestinationWindow } from "client/Api/Apps/common";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ZermeloAppointment, AppointmentKind } from "client/Api/Workspace/LiveInformation/Zermelo/zermeloLiveInformation";
import {
	DateString,
	FontIconName,
	IconColor,
	Icon
} from "client/Components";
import { ListItem, ListItemStyle } from "../ListItem";
import { getSubtitle, getTitle } from "./misc";
import styles from "./ZermeloListItem.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

const {
	Title,
	Info,
	Content,
	Row,
	RightAddition
} = ListItem;

export type ZermeloListItemProps = {
	destinationWindow: DestinationWindow;
	isTeacher: boolean;
	appointment: ZermeloAppointment;
	timezone: TimeZone;
	onClick(): void;
};

export const ZermeloListItem = (props: ZermeloListItemProps): ReactElement => {
	const {
		destinationWindow,
		isTeacher,
		appointment,
		timezone,
		onClick
	} = props;

	const {
		style,
		titleIcon,
		icons
	} = getStylingOptions(appointment);

	const title: string = getTitle(appointment, isTeacher);

	const clickHandler = useCallback(
		(): void => {
			onClick();
			navigateTo(appointment.url, destinationWindow === DestinationWindow.NewWindow);
		},
		[
			appointment.url,
			destinationWindow,
			onClick
		]
	);

	return (
		<ListItem
			title={title}
			style={style}
			trackingId={generateTrackingId("appointment", `|${appointment.id}`)}
			onClick={clickHandler}
			isHoverable
		>
			<div className={styles.timeSpan}>
				<Title>
					<DateString
						value={appointment.startDateTime}
						format="T"
						timezone={timezone}
					/>
				</Title>
				<Info>
					<DateString
						value={appointment.endDateTime}
						format="T"
						timezone={timezone}
					/>
				</Info>
			</div>
			<Content>
				<Row>
					<Title>
						{ titleIcon && (
							<div className={styles.icon}>
								<Icon
									value={{
										kind: "font",
										name: titleIcon.kind,
										color: titleIcon.color
									}}
								/>
							</div>
						)}
						{ title }
					</Title>
					{ appointment.kind !== AppointmentKind.Interlude && (
						<RightAddition>
							<Title>
								{ appointment.lessonNumber }
							</Title>
						</RightAddition>
					)}
				</Row>

				<Row>
					<Info>
						{ getSubtitle(appointment, isTeacher) }
					</Info>

					{ icons.length > 0 && (
						<RightAddition>
							<Info>
								{ icons.map((icon, i) => (
									<div
										key={i}
										className={classNames(
											styles.icon,
											styles.icon_inList
										)}
									>
										<Icon
											value={{
												kind: "font",
												name: icon.kind,
												color:icon.color
											}}
										/>
									</div>
								))}

							</Info>
						</RightAddition>
					)}
				</Row>
			</Content>
		</ListItem>
	);
};
ZermeloListItem.displayName = "ZermeloListItem";

type IconWithColor = {
	kind: FontIconName;
	color: IconColor;
};

type StylingOptions = {
	style: ListItemStyle | undefined;
	titleIcon: IconWithColor | undefined;
	icons: IconWithColor[];
};

function getStylingOptions(appointment: ZermeloAppointment): StylingOptions {
	let style: ListItemStyle | undefined;
	let titleIcon: IconWithColor | undefined;
	const icons: IconWithColor[] = [];

	const {
		choice,
		kind,
		isOnline,
		remarks,
		isCancelled
	} = appointment;

	if (choice.isSet) {
		if (kind === AppointmentKind.Conflict) {
			titleIcon = {
				kind: "triangle-exclamation",
				color: "danger"
			};
		} else {
			titleIcon = {
				kind: choice.value.hasSelected
					? "check"
					: choice.value.canBeChanged
						? "plus"
						: "lock",
				color: choice.value.canBeChanged
					? "success"
					: "secondary"
			};
		}
	}

	switch (kind)
	{
		case AppointmentKind.Exam:
			style = "warning";
			break;
		case AppointmentKind.Activity:
			style = "success";
			break;
		case AppointmentKind.Interlude:
			style = "secondary";
			break;
		case AppointmentKind.Choice:
		case AppointmentKind.Conflict:
		case AppointmentKind.Lesson:
		case AppointmentKind.Unknown:
			// Nothing
			break;
		default:
			throw new UnreachableCaseError(kind);
	}

	if (isOnline) {
		icons.push({
			kind: "video",
			color: "secondary"
		});
	} else if (remarks.length > 0) {
		icons.push({
			kind: "circle-info",
			color: "secondary"
		});
	}

	if (isCancelled) {
		icons.push({
			kind: "xmark",
			color: "danger"
		});
	}

	return {
		style,
		titleIcon,
		icons
	};
}
